import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { concatMap, filter, from, Subscription } from 'rxjs';
import { BaseComponent } from '../../modules/base.component';
import { CartService } from '../../modules/cart/cart.service';
import { LoggerService } from '../../modules/common/logger.service';
import { SponsorService } from '../../modules/common/sponsor.service';
import { UserService } from '../../modules/common/user.service';
import { Constants } from '../../modules/constants';
import { ProductUtil } from '../../modules/util/product-util';
import { ShareableUtil } from '../../modules/social/shareable-util';
import { ISponsorDetails } from '../../modules/social/social-models';
import { FloatingPresence } from '../common/floating-presence/floating-presence-models';
import { FloatingPresenceParam } from '../common/floating-presence/floating-presence-param-models';
import { FloatingPresenceService } from '../common/floating-presence/floating-presence.service';
import { LocaleService } from '../common/locale/locale.service';

@Component({
    selector: 'pwp-section',
    templateUrl: './pwp-section.component.html',
    styleUrls: ['./pwp-section.component.scss'],
    standalone: false,
})
export class PwpSectionComponent extends BaseComponent implements OnInit, OnDestroy {
    private readonly cartService = inject(CartService);
    private readonly sponsorService = inject(SponsorService);
    private readonly floatingPresenceService = inject(FloatingPresenceService);
    private readonly localeService = inject(LocaleService);
    private readonly router = inject(Router);

    constructor(private readonly userService: UserService, loggerService: LoggerService) {
        super(userService, loggerService);
    }

    private routeSub: Subscription;
    private sponsorSub: Subscription;
    private localeSub: Subscription;
    private userSub: Subscription;

    readonly floatingPresence = signal<FloatingPresence | null>(null);
    readonly expanded = signal(false);
    readonly ProductUtil = ProductUtil;
    readonly displayFavorites = signal(false);
    readonly displayProducts = signal<any[]>([]);
    readonly i18nSuffix = signal('');
    readonly sponsorPresent = signal(false);
    readonly isMainRoute = signal(false);
    // The PWP bar belongs to the pre-attribution-flow experience; when the attribution
    // flow is enabled (config.showCartPriceBreakdown) the attribution bar takes over and
    // the PWP bar must stay hidden. Held as a signal (distinct from BaseComponent's plain
    // showCartPriceBreakdown field) so the visible computed reacts to it.
    readonly cartPriceBreakdownEnabled = signal(false);
    readonly visible = computed(() => this.sponsorPresent() && this.isMainRoute() && !this.cartPriceBreakdownEnabled());

    featuredProducts: any[] = [];

    ngOnInit(): void {
        this.isMainRoute.set(this.checkIsMainRoute(this.router.url));

        this.routeSub = this.router.events.pipe(
            filter((evt): evt is NavigationEnd => evt instanceof NavigationEnd),
        ).subscribe(() => {
            this.isMainRoute.set(this.checkIsMainRoute(this.router.url));
            this.expanded.set(false);
        });

        this.sponsorSub = this.sponsorService.sponsorVisible$().subscribe((sponsorDetails) => {
            this.onSponsorChanged(sponsorDetails);
        });

        this.localeSub = this.localeService.getLocaleObservable().subscribe(() => {
            if (this.floatingPresenceService.getActivePwpSite()) {
                this.loadContent();
            }
        });

        this.userSub = this.userService.watchShopUser().subscribe((user) => {
            this.cartPriceBreakdownEnabled.set(!!user?.config?.showCartPriceBreakdown);
        });
    }

    ngOnDestroy(): void {
        this.routeSub?.unsubscribe();
        this.sponsorSub?.unsubscribe();
        this.localeSub?.unsubscribe();
        this.userSub?.unsubscribe();
    }

    private onSponsorChanged(sponsorDetails: ISponsorDetails): void {
        const hasSponsor = ShareableUtil.isSponsorDetailsPresent(sponsorDetails);
        this.sponsorPresent.set(hasSponsor);

        if (hasSponsor && sponsorDetails?.site) {
            const previousPwpSite = this.floatingPresenceService.getActivePwpSite();
            if (previousPwpSite !== sponsorDetails.site) {
                // Clear any previous profile so the template falls back to the placeholder
                // while we fetch the new one.
                this.floatingPresence.set(null);
            }
            this.floatingPresenceService.setActivePwpSite(sponsorDetails.site);
            this.loadContent();
        } else if (!hasSponsor) {
            this.floatingPresenceService.setActivePwpSite(null);
            this.floatingPresence.set(null);
        }
    }

    private async loadContent(): Promise<void> {
        this.showPricing = this.localeService.getLocaleObject().hasRetailShopping;

        try {
            const fp = await this.floatingPresenceService.loadFloatingPresence();

            this.i18nSuffix.set(fp.country === 'IN' ? '.IN' : '');

            if (fp.params) {
                this.setMyStory(fp, fp.params);

                if (fp.params.favorites?.value !== null && fp.params.favorites?.visible) {
                    this.displayFavorites.set(true);
                    await this.loadProducts(fp.params);
                } else {
                    this.displayFavorites.set(false);
                    try {
                        const featured = await this.floatingPresenceService.loadFeaturedProducts();
                        this.setFeaturedProducts(this.productImagesSortedByImageOrder(featured));
                    } catch (err) {
                        console.error('Failed to load featured products', err);
                    }
                }

                if (fp.params.gtm?.value) {
                    this.loadGtmScript(fp.params.gtm.value);
                }
            }

            this.floatingPresence.set(fp);
        } catch (err) {
            console.error('Failed to load floating presence', err);
        }
    }

    /**
     * Copies the story for the current language into the single `mystory` param the template reads.
     * Profiles do not always carry a base `mystory` entry to write into — it varies by market — so the
     * slot is created when there is a story to show and left absent when there is not.
     */
    private setMyStory(fp: FloatingPresence, params: Record<string, any>): void {
        const currentLanguage = this.localeService.getLocale().split('-')[0];
        const localized: FloatingPresenceParam = params[`mystory_${currentLanguage}`];
        const anyStoryVisible: boolean = Object.keys(params)
            .filter((key) => key.includes('mystory_'))
            .some((key) => params[key]?.visible === true);

        const value: string = anyStoryVisible && localized?.value != null ? localized.value : null;

        if (fp.params.mystory) {
            fp.params.mystory.value = value;
        } else if (value !== null) {
            fp.params.mystory = {...localized, param: 'mystory', value};
        }
    }

    async loadProducts(params: Record<string, any>): Promise<void> {
        this.featuredProducts = [];
        this.displayProducts.set([]);
        const favoritesList: string[] = params.favorites.value.split(',');

        try {
            const favoritePromises = favoritesList.map((usanaId) =>
                this.floatingPresenceService.loadProduct(usanaId).catch((err) => {
                    console.error(`Failed to load product ${usanaId}`, err);
                    return null;
                }),
            );

            const favorites = await Promise.all(favoritePromises);

            const newFeaturedProducts: any[] = [];
            favorites.forEach((favorite: any, index: number) => {
                if (favorite?.products) {
                    const usanaIdPrefix = favoritesList[index].split('.')[0];
                    for (const key in favorite.products) {
                        if (favorite.products[key].usanaId.split('.')[0] === usanaIdPrefix) {
                            newFeaturedProducts.push(favorite.products[key]);
                        }
                    }
                }
            });

            this.setFeaturedProducts(this.productImagesSortedByImageOrder(newFeaturedProducts));
        } catch (err) {
            console.error('Error loading favorites', err);
        }
    }

    productImagesSortedByImageOrder(products: any[]): any[] {
        return products.map((product) => {
            if (product.productImages) {
                product.productImages.sort((a: any, b: any) => a.imageOrder - b.imageOrder);
                product.productImages = product.productImages.filter((image: any) => image.type.imageSize === 'SM');
            }
            return product;
        });
    }

    removeDuplicateProducts(products: any[]): any[] {
        return products.filter((v, i, a) => a.findIndex((t) => t.usanaId.split('.')[0] === v.usanaId.split('.')[0]) === i);
    }

    loadGtmScript(gtmId: string): void {
        if (document.getElementById('pfpGtm')) {
            return;
        }

        const node = document.createElement('script');
        node.type = 'text/javascript';
        node.id = 'pfpGtm';
        node.text = `
      window.fpDataLayer = window.fpDataLayer || [];
      fpDataLayer.push({ 'gtm.blocklist': ['html'] });
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push(
        {'gtm.start': new Date().getTime(),event:'gtm.js'}
      );var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','fpDataLayer', '${gtmId.trim()}');
    `;
        document.head.appendChild(node);
    }

    /**
     * Ensures a social/external URL has a protocol so the browser does not
     * treat it as a relative path (e.g. "www.facebook.com/foo" would otherwise
     * be appended to the current page URL).
     */
    getExternalHref(value: string | null | undefined): string {
        if (!value) {
            return '';
        }
        const trimmed = value.trim();
        if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) || trimmed.startsWith('//')) {
            return trimmed;
        }
        return `https://${trimmed}`;
    }

    hasValidProfileImage(): boolean {
        const pic = this.floatingPresence()?.params?.['pic']?.value;
        return !!pic && (pic.startsWith('/image-service/') || pic.startsWith('https://images.usana.com/'));
    }

    hasAnyContactInfo(): boolean {
        const params = this.floatingPresence()?.params;
        if (!params) {
            return false;
        }
        return (
            !!params['phone']?.visible ||
            !!params['email']?.visible ||
            (!!params['facebook']?.value && !!params['facebook']?.visible) ||
            (!!params['instagram']?.value && !!params['instagram']?.visible) ||
            (!!params['pinterest']?.value && !!params['pinterest']?.visible) ||
            (!!params['linkedin']?.value && !!params['linkedin']?.visible)
        );
    }

    toggle(): void {
        this.expanded.update((v) => !v);
    }

    showPriceByProduct(product: any): string {
        if (!product) {
            return '';
        }
        return product.price.priceDisplay;
    }

    addAllToCart(): void {
        const products = this.removeDuplicateProducts(this.featuredProducts);
        from(products).pipe(
            concatMap((product) => this.cartService.addToCart(product)),
        ).subscribe({
            complete: () => {
                this.router.navigateByUrl(this.CART_OVERVIEW_ROUTE);
            },
            error: (err) => {
                console.error('Error adding PWP products to cart', err);
            },
        });
    }

    private checkIsMainRoute(url: string): boolean {
        const cleanUrl = url.split('?')[0];
        //For now don't show it other than dotcom, in the future we will probably show it so keep other code
        if (!cleanUrl.includes(Constants.URL_BASE_DOTCOM)) {
            return false;
        }
        const stripBase = (route: string) => route.replace(`/${Constants.URL_BASE_CART}`, '');
        return !cleanUrl.endsWith(stripBase(this.ENROLL_ASSOCIATE_ROUTE))
            && !cleanUrl.endsWith(stripBase(this.CHECKOUT_ROUTE))
            && !cleanUrl.includes(stripBase(this.PAYMENT_CALLBACK_ROUTE))
            && !cleanUrl.includes(stripBase(this.CHECKOUT_CONFIRM_ROUTE));
    }

    private setFeaturedProducts(products: any[]): void {
        this.featuredProducts = products;
        this.displayProducts.set(this.removeDuplicateProducts(products));
    }
}
